import { Controller, useForm, useFormContext } from 'react-hook-form'
import DialogModal from 'components/generic/modals/dialog-modal'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material_modern'
import React, { useEffect } from 'react'

interface FeeOrPromotionModalProps {
  open: boolean
  onClose: () => void
  selectedFeeOrPromotionId: string | undefined
  onFeeOrPromotionUpdateSubmit?: () => void
}

interface FeeOrPromotionForm {
  id: string
  name: string
  type: 'FEE' | 'PROMOTION'
  amountType: 'FLAT' | 'PERCENT'
  amount: number
  eligiblePaymentTypes: string[]
}

const FeeOrPromotionModal: React.FC<FeeOrPromotionModalProps> = ({
  open,
  onClose,
  selectedFeeOrPromotionId,
  onFeeOrPromotionUpdateSubmit,
}) => {
  const parentFormCtxt = useFormContext()
  const { control, getValues, reset, watch } = useForm<FeeOrPromotionForm>({
    defaultValues: parentFormCtxt.getValues(`feeOrPromotion.${selectedFeeOrPromotionId}`) || {},
  })

  // Whenever the parent gives us a new row, update the local form state
  useEffect(() => {
    if (selectedFeeOrPromotionId) {
      reset(parentFormCtxt.getValues(`feeOrPromotion.${selectedFeeOrPromotionId}`))
    }
  }, [selectedFeeOrPromotionId, reset, parentFormCtxt])

  const handleSave = () => {
    const updatedFeeOrPromotionRow = getValues()
    parentFormCtxt.setValue(`feeOrPromotion.${selectedFeeOrPromotionId}`, updatedFeeOrPromotionRow, {
      shouldDirty: true,
    })
    onFeeOrPromotionUpdateSubmit && onFeeOrPromotionUpdateSubmit()
    onClose()
  }

  const name = watch('name') || ''
  const type = watch('type') || ''
  const amountType = watch('amountType') || ''

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title="Edit Fee or Promotion"
      buttonTitle="ok"
      secondaryButtonTitle="Cancel"
      onSave={handleSave}
      dialogStyles={{
        '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
      }}
    >
      {selectedFeeOrPromotionId && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6">
            {name} ({type.toLowerCase()})
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl sx={{ flex: 1.5 }}>
              <InputLabel id="amount-type-label">Amount Type</InputLabel>
              <Controller
                name="amountType"
                control={control}
                render={({ field }) => (
                  <Select labelId="amount-type-label" label="Amount Type" {...field}>
                    <MenuItem value="FLAT">Flat</MenuItem>
                    <MenuItem value="PERCENT">Percent</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <FormControl>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    sx={{ height: '56px' }}
                    exclusive
                    value={field.value}
                    onChange={(_, newValue) => {
                      if (newValue !== null) field.onChange(newValue)
                    }}
                    aria-label="Fee or Promotion"
                  >
                    <ToggleButton value="PROMOTION">Promotion</ToggleButton>
                    <ToggleButton value="FEE">Fee</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </FormControl>
            <FormControl sx={{ flex: 1.5 }}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Amount"
                    type="number"
                    inputProps={{ step: 'any' }}
                    InputProps={{
                      startAdornment:
                        amountType === 'FLAT' ? <Typography sx={{ ml: 1, mr: 0.5 }}>$</Typography> : undefined,
                      endAdornment:
                        amountType === 'PERCENT' ? <Typography sx={{ ml: 0.5, mr: 1 }}>%</Typography> : undefined,
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>
        </Box>
      )}
    </DialogModal>
  )
}

export default FeeOrPromotionModal
